import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Sparkles, X, Send, RotateCcw, ArrowUpRight } from "lucide-react";
const API = process.env.REACT_APP_BACKEND_URL;
const FUCHSIA = "#E0119D";
const FUCHSIA_DARK = "#B10B7C";
const SS_KEY = "landy_conv_v1";
const SS_SURPRISE = "landy_surprise_seen_v1";

const prefersReduced = () =>
  typeof window !== "undefined" && window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Formattazione leggera: **grassetto**, link cliccabili (interni stessa scheda)
function renderText(text) {
  const parts = [];
  const regex = /(\*\*[^*]+\*\*)|((?:https?:\/\/|www\.)[^\s)]+)/g;
  let last = 0, m, i = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("**")) {
      parts.push(<strong key={i++}>{tok.slice(2, -2)}</strong>);
    } else {
      let href = tok.startsWith("http") ? tok : `https://${tok}`;
      const internal = href.includes("hostland.vitadahost.it") || href.includes("vitadahost.it");
      parts.push(
        <a key={i++} href={href} target={internal ? "_self" : "_blank"} rel="noopener noreferrer"
           style={{ color: FUCHSIA, fontWeight: 700, textDecoration: "underline" }}>{tok}</a>
      );
    }
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

// Effetto "macchina da scrivere" per le risposte (come i bot AI).
function Typewriter({ text, onType, onDone }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (n >= text.length) { onDone && onDone(); return; }
    const id = setTimeout(() => { setN(v => Math.min(text.length, v + 3)); onType && onType(); }, 15);
    return () => clearTimeout(id);
  }, [n, text]);
  return <span>{text.slice(0, n)}{n < text.length && <span className="landy-caret">▋</span>}</span>;
}

export default function Landy() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [welcome, setWelcome] = useState("");
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [closed, setClosed] = useState(false);
  const [surpriseEnabled, setSurpriseEnabled] = useState(true);
  const sessionId = useRef(
    (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID()
      : "s_" + Math.random().toString(36).slice(2)
  );
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Mostra solo sulla home
  const onHome = location.pathname === "/";

  useEffect(() => {
    if (!onHome) return;
    fetch(`${API}/api/assistant/bootstrap`).then(r => r.json()).then(d => {
      setWelcome(d.welcome || "");
      setSuggested(d.suggested || []);
      setSurpriseEnabled(d.surprise_enabled !== false);
    }).catch(() => {});
    try {
      const saved = sessionStorage.getItem(SS_KEY);
      if (saved) setMessages(JSON.parse(saved));
    } catch (e) {}
  }, [onHome]);

  useEffect(() => {
    try { sessionStorage.setItem(SS_KEY, JSON.stringify(messages)); } catch (e) {}
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = useCallback(async (text, suggestedKey) => {
    if (loading || closed) return;
    const userText = suggestedKey ? suggested.find(s => s.key === suggestedKey)?.question : text;
    if (!suggestedKey && !userText?.trim()) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", text: userText }]);
    setLoading(true);
    try {
      const history = messages.slice(-4).map(m => ({ role: m.role, text: m.text }));
      const r = await fetch(`${API}/api/assistant/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId.current,
          message: suggestedKey ? "" : userText,
          suggested_key: suggestedKey || "",
          history,
        }),
      });
      const d = await r.json();
      setMessages(prev => {
        const next = [...prev, { role: "assistant", text: d.answer || "" }];
        // Sorpresa Hostlander: una sola volta per sessione
        let seen = false;
        try { seen = sessionStorage.getItem(SS_SURPRISE) === "1"; } catch (e) {}
        if (d.surprise && surpriseEnabled && !seen) {
          try { sessionStorage.setItem(SS_SURPRISE, "1"); } catch (e) {}
          next.push({ role: "surprise", text: d.surprise_message, url: d.surprise_url });
        }
        return next;
      });
      if (d.closed) setClosed(true);
    } catch (e) {
      setMessages(prev => [...prev, { role: "assistant", text: "Scusami, problema di connessione. Riprova tra poco." }]);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [loading, closed, messages, suggested, surpriseEnabled]);

  const restart = () => {
    setMessages([]); setClosed(false); setInput("");
    try { sessionStorage.removeItem(SS_KEY); sessionStorage.removeItem(SS_SURPRISE); } catch (e) {}
    sessionId.current = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : "s_" + Math.random().toString(36).slice(2);
  };

  const surpriseClick = () => {
    fetch(`${API}/api/assistant/surprise-click`, { method: "POST" }).catch(() => {});
  };

  const scrollDown = () => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; };
  const markTyped = (idx) => setMessages(prev => prev.map((x, i) => i === idx ? { ...x, _typed: true } : x));

  if (!onHome) return null;
  const anim = !prefersReduced();

  return (
    <>
      {/* Pill fluttuante centrata in basso, fucsia */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="landy-open-btn"
          aria-label="Apri Landy, l'assistente di Hostland"
          className={anim ? "landy-pulse" : ""}
          style={{
            position: "fixed", left: "50%", transform: "translateX(-50%)",
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 18px)", zIndex: 60,
            display: "inline-flex", alignItems: "center", gap: 9,
            padding: "11px 22px 11px 13px", borderRadius: 999, border: "none", cursor: "pointer",
            background: `linear-gradient(135deg, ${FUCHSIA} 0%, ${FUCHSIA_DARK} 100%)`,
            color: "#fff", fontWeight: 800, fontSize: 15,
            boxShadow: `0 14px 34px -8px ${FUCHSIA}aa, 0 4px 12px rgba(0,0,0,.25)`,
          }}
        >
          <img src="/hostland-logo.png" alt="Hostland" style={{ width: 30, height: 30, borderRadius: 8, objectFit: "cover", background: "#fff", padding: 2 }} />
          Chiedi a Landy
        </button>
      )}

      {open && (
        <div
          data-testid="landy-panel"
          role="dialog"
          aria-label="Landy, assistente di Hostland"
          style={{
            position: "fixed", zIndex: 70, background: "#fff",
            display: "flex", flexDirection: "column", overflow: "hidden",
            animation: anim ? "landyIn .22s ease" : "none",
          }}
          className="landy-panel"
        >
          {/* Header */}
          <div style={{ background: `linear-gradient(135deg, ${FUCHSIA} 0%, ${FUCHSIA_DARK} 100%)`, color: "#fff", padding: "12px 14px 10px", flexShrink: 0 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                <img src="/hostland-logo.png" alt="Hostland" style={{ width: 46, height: 46, borderRadius: 11, objectFit: "cover", display: "block", flexShrink: 0 }} />
                <div>
                  <div style={{ fontWeight: 900, fontSize: 24, lineHeight: 1 }}>Landy</div>
                  <div style={{ fontSize: 11.5, opacity: .95, marginTop: 5, lineHeight: 1.35 }}>
                    <b>L'assistente di Hostland</b> Ho imparato tantissimo e ogni giorno di più ma potrei commettere qualche errore a volte.
                  </div>
                </div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="landy-close-btn" aria-label="Chiudi Landy"
                style={{ background: "transparent", border: "none", color: "#fff", cursor: "pointer", padding: 4, flexShrink: 0, alignSelf: "flex-start" }}>
                <X className="w-6 h-6" />
              </button>
            </div>
            <div style={{ textAlign: "right", marginTop: 4 }}>
              <a href="/privacy" target="_self" style={{ color: "#fff", opacity: .9, fontSize: 11, textDecoration: "underline", display: "inline-block" }}>Informativa privacy</a>
            </div>
          </div>

          {/* Messaggi */}
          <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: 16, background: "#FAFAFA" }}>
            {/* Benvenuto */}
            <div style={bubbleBot}>{welcome}</div>
            {messages.length === 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
                {suggested.map(s => (
                  <button key={s.key} data-testid={`landy-suggested-${s.key}`}
                    onClick={() => send(null, s.key)}
                    style={chip}>{s.question}</button>
                ))}
              </div>
            )}
            {messages.map((m, i) => {
              if (m.role === "user") return <div key={i} style={bubbleUser}>{m.text}</div>;
              if (m.role === "surprise") return (
                <div key={i} data-testid="landy-surprise" style={{ ...bubbleBot, border: `2px solid ${FUCHSIA}`, background: "#FFF0F9", marginTop: 12 }}>
                  <div style={{ marginBottom: 12 }}>
                    {m._typed ? m.text : <Typewriter text={m.text} onType={scrollDown} onDone={() => markTyped(i)} />}
                  </div>
                  {m._typed && (
                    <a href={m.url} target="_self" onClick={surpriseClick} data-testid="landy-hostlander-btn"
                      style={{ display: "inline-flex", alignItems: "center", gap: 6, background: `linear-gradient(135deg, ${FUCHSIA} 0%, ${FUCHSIA_DARK} 100%)`, color: "#fff", fontWeight: 800, padding: "11px 20px", borderRadius: 999, textDecoration: "none", fontSize: 14.5 }}>
                      Diventa Hostlander <ArrowUpRight className="w-4 h-4" />
                    </a>
                  )}
                </div>
              );
              return (
                <div key={i} style={bubbleBot}>
                  {m._typed ? renderText(m.text) : <Typewriter text={m.text} onType={scrollDown} onDone={() => markTyped(i)} />}
                </div>
              );
            })}
            {loading && <div style={{ ...bubbleBot, color: "#999" }}><span className="landy-typing">Landy sta scrivendo…</span></div>}
          </div>

          {/* Input / Ricomincia */}
          <div style={{ padding: 12, borderTop: "1px solid #eee", flexShrink: 0, background: "#fff" }}>
            {closed ? (
              <button onClick={restart} data-testid="landy-restart-btn" style={{ ...sendBtn, width: "100%", justifyContent: "center" }}>
                <RotateCcw className="w-4 h-4" /> Ricomincia
              </button>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <button onClick={restart} data-testid="landy-restart-inline" aria-label="Ricomincia" title="Ricomincia"
                  style={{ background: "#f3f4f6", border: "none", borderRadius: 10, width: 40, height: 40, cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <RotateCcw className="w-4 h-4" />
                </button>
                <input ref={inputRef} value={input} maxLength={800}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") send(input); }}
                  placeholder="Scrivi una domanda…" data-testid="landy-input"
                  style={{ flex: 1, border: "1.5px solid #e5e7eb", borderRadius: 12, padding: "11px 14px", fontSize: 14, outline: "none" }}
                  onFocus={(e) => e.target.style.borderColor = FUCHSIA} onBlur={(e) => e.target.style.borderColor = "#e5e7eb"} />
                <button onClick={() => send(input)} disabled={loading || !input.trim()} data-testid="landy-send-btn"
                  aria-label="Invia" style={{ ...sendBtn, opacity: (loading || !input.trim()) ? .5 : 1 }}>
                  <Send className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes landyIn { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes landyPulse {
          0% { transform: translateX(-50%) scale(1); box-shadow: 0 14px 34px -8px ${FUCHSIA}aa, 0 0 0 0 ${FUCHSIA}66; }
          70% { transform: translateX(-50%) scale(1.04); box-shadow: 0 16px 38px -8px ${FUCHSIA}aa, 0 0 0 14px ${FUCHSIA}00; }
          100% { transform: translateX(-50%) scale(1); box-shadow: 0 14px 34px -8px ${FUCHSIA}aa, 0 0 0 0 ${FUCHSIA}00; }
        }
        .landy-pulse { animation: landyPulse 2s ease-in-out infinite; }
        .landy-pulse:hover { animation-play-state: paused; transform: translateX(-50%) scale(1.05); }
        .landy-typing { animation: landyBlink 1.2s infinite; }
        .landy-caret { display: inline-block; width: 7px; margin-left: 1px; color: ${FUCHSIA}; animation: landyBlink .8s steps(1) infinite; }
        @keyframes landyBlink { 0%,100%{opacity:.4} 50%{opacity:1} }
        .landy-panel {
          right: 16px; left: auto; bottom: 16px;
          width: 380px; height: 600px; max-height: calc(100vh - 32px);
          border-radius: 20px; box-shadow: 0 24px 60px -12px rgba(0,0,0,.4);
        }
        @media (max-width: 640px) {
          .landy-panel {
            inset: 0 !important; width: 100% !important; height: 100% !important;
            max-height: 100% !important; border-radius: 0 !important;
            padding-top: env(safe-area-inset-top, 0px);
            padding-bottom: env(safe-area-inset-bottom, 0px);
          }
        }
      `}</style>
    </>
  );
}

const bubbleBot = { background: "#fff", border: "1px solid #eee", borderRadius: "6px 18px 18px 18px", padding: "13px 16px", fontSize: 15, lineHeight: 1.65, color: "#18181B", marginBottom: 14, maxWidth: "90%", boxShadow: "0 2px 6px rgba(0,0,0,.05)", letterSpacing: ".01em" };
const bubbleUser = { background: "#18181B", color: "#fff", borderRadius: "18px 18px 6px 18px", padding: "12px 16px", fontSize: 15, lineHeight: 1.55, marginBottom: 14, maxWidth: "90%", marginLeft: "auto" };
const chip = { background: "#fff", border: `1.5px solid ${FUCHSIA}`, color: FUCHSIA, borderRadius: 999, padding: "9px 15px", fontSize: 13.5, fontWeight: 700, cursor: "pointer", textAlign: "left" };
const sendBtn = { background: `linear-gradient(135deg, ${FUCHSIA} 0%, ${FUCHSIA_DARK} 100%)`, color: "#fff", border: "none", borderRadius: 12, width: 44, height: 44, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0, gap: 6, fontWeight: 800, fontSize: 14 };
